import { ArrowDown, ArrowUp, ListPlus, ScanSearch } from 'lucide-react';
import ItemImage from './ItemImage';
import { LaneChip, DiagnosisChip, PriorityBadge, CompletenessBar, ReturnBadge } from './Tags';
import ShortlistPicker from '../shortlists/ShortlistPicker';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { fmtEur, fmtInt, fmtPrice, variantLabel, discountPct, fmtShopPrice } from '../../lib/format';
import { cn } from '../../lib/utils';

function SortHead({ label, sortKeys, sort, onSort, className }) {
  // sortKeys: [primary] or [desc, asc] when both directions exist
  const active = sortKeys.includes(sort);
  const next = active && sortKeys.length > 1 ? sortKeys[(sortKeys.indexOf(sort) + 1) % sortKeys.length] : sortKeys[0];
  const asc = sort === 'completeness';
  return (
    <TableHead className={cn('whitespace-nowrap', className)}>
      <button
        type="button"
        onClick={() => onSort?.(next)}
        className={cn('inline-flex items-center gap-1 hover:text-foreground', active && 'text-foreground')}
        disabled={!onSort}
      >
        {label}
        {active && (asc ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </TableHead>
  );
}

export default function ItemTable({ items, sort, onSort, onOpen, onFindSimilar }) {
  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-14" />
            <TableHead>Product</TableHead>
            <TableHead>Lane / diagnosis</TableHead>
            <TableHead>Priority</TableHead>
            <SortHead label="In stock" sortKeys={['qty']} sort={sort} onSort={onSort} className="text-right" />
            <SortHead label="Sold 6M" sortKeys={['sold6']} sort={sort} onSort={onSort} className="text-right" />
            <SortHead label="Sold 12M" sortKeys={['sold12']} sort={sort} onSort={onSort} className="text-right" />
            <SortHead label="Unit cost" sortKeys={['cost']} sort={sort} onSort={onSort} className="text-right" />
            <TableHead className="text-right">Price</TableHead>
            <SortHead label="Value" sortKeys={['value']} sort={sort} onSort={onSort} className="text-right" />
            <SortHead label="Age" sortKeys={['age']} sort={sort} onSort={onSort} className="text-right" />
            <SortHead label="Listing" sortKeys={['-completeness', 'completeness']} sort={sort} onSort={onSort} />
            <TableHead className="w-20" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.odoo_product_id} className="cursor-pointer" onClick={() => onOpen?.(it)}>
              <TableCell className="py-1.5">
                <ItemImage src={it.image_url} alt="" className="h-11 w-11 border" rounded="rounded-md" iconClassName="h-4 w-4" />
              </TableCell>
              <TableCell className="min-w-[220px] max-w-[340px]">
                <div className="truncate text-[11px] uppercase tracking-wide text-muted-foreground">{it.brand}</div>
                <div className="truncate font-medium">{it.title}</div>
                <div className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
                  {variantLabel(it.variant_title)}
                  {it.supplier_return_candidate && <ReturnBadge />}
                  {it.visual_sim != null && <span className="tabular-nums text-sky-700 dark:text-sky-300">look {Math.round(it.visual_sim * 100)}</span>}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-col items-start gap-1">
                  <LaneChip lane={it.exit_lane} />
                  <DiagnosisChip diagnosis={it.diagnosis} reason={it.diagnosis_reason} />
                </div>
              </TableCell>
              <TableCell>
                <PriorityBadge priority={it.priority} short />
              </TableCell>
              <TableCell className="text-right tabular-nums">{fmtInt(it.free_qty)}</TableCell>
              <TableCell className={cn('text-right tabular-nums', !it.sold_6m && 'text-muted-foreground')}>{fmtInt(it.sold_6m)}</TableCell>
              <TableCell className={cn('text-right tabular-nums', !it.sold_12m && 'text-muted-foreground')}>{fmtInt(it.sold_12m)}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtPrice(it.unit_cost)}</TableCell>
              <TableCell className="text-right tabular-nums">
                <div className={cn(discountPct(it.price, it.rrp) != null && 'font-medium text-red-600 dark:text-red-400')}>{fmtShopPrice(it.price)}</div>
                {discountPct(it.price, it.rrp) != null && (
                  <div className="whitespace-nowrap text-[11px] text-muted-foreground">
                    <span className="line-through">{fmtShopPrice(it.rrp)}</span> −{discountPct(it.price, it.rrp)}%
                  </div>
                )}
              </TableCell>
              <TableCell className="text-right font-medium tabular-nums">{fmtEur(it.free_value)}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtInt(it.age_days)}d</TableCell>
              <TableCell className="w-28">
                <CompletenessBar value={it.completeness} missing={it.missing_fields} />
              </TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-end gap-0.5">
                  {onFindSimilar && (
                    <button
                      type="button"
                      onClick={() => onFindSimilar(it)}
                      className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                      title="Find similar look"
                    >
                      <ScanSearch className="h-4 w-4" />
                    </button>
                  )}
                  <ShortlistPicker ids={[it.odoo_product_id]}>
                    <button
                      type="button"
                      className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                      title="Add to shortlist"
                    >
                      <ListPlus className="h-4 w-4" />
                    </button>
                  </ShortlistPicker>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
